export type SchemaKind = 'report' | 'record' | 'outline' | 'unknown'

export interface SchemaDetection {
  kind: SchemaKind
  confidence: number
}

const identityFields = new Set(['name', 'id', 'title', 'email', 'slug'])
const reportFields = new Set([
  'status',
  'priority',
  'next_step',
  'report_id',
  'findings',
  'recommendations',
  'recommended_actions',
  'inspection',
  'severity',
])

export function detectSchema(value: unknown, _source?: string): SchemaDetection {
  const reportConfidence = getReportConfidence(value)

  if (reportConfidence > 0) {
    return {
      kind: 'report',
      confidence: reportConfidence,
    }
  }

  if (isRecordLike(value)) {
    return {
      kind: 'record',
      confidence: 0.82,
    }
  }

  return {
    kind: 'outline',
    confidence: 1,
  }
}

function getReportConfidence(value: unknown): number {
  if (!isPlainObject(value)) {
    return 0
  }

  const entries = Object.entries(value)

  if (entries.length < 3) {
    return 0
  }

  const matchedCanonicalFields = entries.filter(([key]) => reportFields.has(key.toLowerCase())).length

  if (matchedCanonicalFields === 0) {
    return 0
  }

  return Math.min(0.96, 0.58 + matchedCanonicalFields * 0.08)
}

function isRecordLike(value: unknown): value is Record<string, unknown> {
  if (!isPlainObject(value)) {
    return false
  }

  const entries = Object.entries(value)
  const scalarEntries = entries.filter(([, entryValue]) => isTopLevelScalar(entryValue))

  if (scalarEntries.length === 0) {
    return false
  }

  const hasIdentityField = scalarEntries.some(([key]) => identityFields.has(key.toLowerCase()))

  return hasIdentityField || scalarEntries.length >= 5
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isTopLevelScalar(value: unknown): boolean {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value)
}
